<!-- src/components/MainNav.vue -->
<script setup>
/**
 * MainNav is the top-level navigation:
 * - CP
 * - PIM
 * - SAP
 * - BMD
 * - BASE
 *
 * Controlled entirely by AppShell:
 * props.open  = panel visibility
 * props.active = currently selected system
 */

const props = defineProps({
  open: { type: Boolean, default: false },
  active: { type: String, default: "PIM" }
});

const emit = defineEmits(["select", "close"]);

// Available systems
const systems = Object.freeze([
  { key: "PIM",  label: "Node.Zone PIM" },
  { key: "CP",   label: "ComfortPages" },
  { key: "SAP",  label: "SAP" },  
  { key: "BMD",  label: "BMD" },
  { key: "BASE", label: "Base.com" }
]);

// Handle selection and auto-close
function handleSelect(key) {
  emit("select", key);
  emit("close");
}
</script>

<template>
  <!-- Backdrop -->
  <transition name="fade">
    <div
      v-if="open"
      class="mainnav-backdrop"
      @click="emit('close')"
    />
  </transition>

  <!-- Slide-down panel -->
  <transition name="slide-down">
    <nav
      v-if="open"
      class="mainnav-panel"
      role="navigation"
      aria-label="Primary Navigation"
    >
      <ul>
        <li
          v-for="sys in systems"
          :key="sys.key"
          :class="{ active: sys.key === active }"
          @click="handleSelect(sys.key)"
        >
          {{ sys.label }}
        </li>
      </ul>
    </nav>
  </transition>
</template>

<style lang="less" scoped>
/* Backdrop */
.mainnav-backdrop {
  position: fixed;
  inset: 0;
  background: fade(@accent, 50%);
  z-index: 50;
}

/* Panel */
.mainnav-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  background: #fff;
  padding: 1rem 1.5rem;
  z-index: 60;

  border-bottom: 1px solid @accent;

  &:after{
    position: absolute;
    z-index: 62;
    left: 20px;
    top: 0px;
    font-family: 'icomoon' !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    font-size: 3.5rem;
    color: @accent;
    display: inline;
    /* Better Font Rendering =========== */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\e9ff";

  }

}

.mainnav-panel ul {
  display: block;
  list-style: none;
  margin: 0 50px 0 auto;
  max-width: 300px;
  padding: 0;
}

.mainnav-panel li {
  padding: 0.75rem 0;
  padding-left: 12px;
  font-size: 1.1rem;
  cursor: pointer;
  user-select: none;

  color: #333;
  transition: color 0.2s ease;
}

.mainnav-panel li:hover {
  color: @accent;
}

.mainnav-panel li.active {
  color: darken(@accent, 10%);
  font-weight: bold;
  border-left: 4px solid @accent;
  padding-left: 8px;
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-20px);
  opacity: 0;
}
</style>
